import React from "react";
import { PROJECTION_DISCLAIMER } from "../../utils/subprocessRedesign";

// Small shared pieces for the sub-process redesign projection, used by both the RL
// redesign panel and the comparison report so a child reads identically in either
// place. Rendering-only — the logic lives in utils/subprocessRedesign.js.

/**
 * Chip naming the child a heuristic was applied inside. Required on every
 * source="subprocess" row: without it the row is indistinguishable from one applied
 * to the main process.
 */
export function ChildChip({ name, title }) {
  return (
    <span
      title={title || `Applied inside the sub-process "${name}", not to this process directly`}
      style={{
        display: "inline-block", fontSize: 9.5, fontWeight: 700, borderRadius: 999,
        padding: "1px 7px", whiteSpace: "nowrap", background: "#ede9fe",
        color: "#5b21b6", border: "1px solid #ddd6fe", cursor: "help",
      }}
    >
      ⤷ {name}
    </span>
  );
}

/**
 * Rule B — required wherever projected figures are shown. The numbers describe a
 * process state that does not exist in the CMS yet.
 */
export function ProjectionBanner({ projection, compact }) {
  if (!projection) return null;
  const count = projection.applied_heuristic_count;
  return (
    <div
      style={{
        marginBottom: compact ? 8 : 14, padding: compact ? "6px 10px" : "10px 14px",
        background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10,
        color: "#5b21b6", fontSize: compact ? 11 : 12.5,
      }}
    >
      <strong>Projected figures.</strong> {PROJECTION_DISCLAIMER}
      {count != null && (
        <span> {count} heuristic{count === 1 ? "" : "s"} applied inside sub-processes.</span>
      )}
      {projection.depth && (
        <div style={{ marginTop: 4, fontSize: compact ? 10 : 11, color: "#7c3aed" }}>
          Scope: {projection.depth}.
        </div>
      )}
    </div>
  );
}

/**
 * `priced === false` means the engine ran the sub-process redesigns but their effect
 * never reached this column's scored figures. Badging that "PROJECTED" claims an
 * improvement the numbers underneath do not contain — the opposite of the mislabelling
 * this badge exists to prevent — so the two states must not share a label.
 */
export function ProjectedColumnBadge({ priced = true }) {
  const label = priced ? "PROJECTED" : "SUB-PROCESSES RUN, NOT PRICED IN";
  return (
    <span
      title={
        priced
          ? "This column's figures assume the sub-process redesigns are adopted. The other engines describe the process as it stands today."
          : "This engine redesigned the sub-processes, but the figures in this column are scored on this process alone — see the note below."
      }
      style={{
        display: "inline-block", fontSize: 10, fontWeight: 800, borderRadius: 999,
        padding: "2px 9px", whiteSpace: "nowrap",
        background: priced ? "#ede9fe" : "#fef3c7",
        color: priced ? "#5b21b6" : "#92400e",
        border: `1px solid ${priced ? "#c4b5fd" : "#fcd34d"}`,
        cursor: "help",
      }}
    >
      {label}
    </span>
  );
}

/**
 * Children the backend could not redesign. Every field beyond id/name/status is
 * optional on these, so nothing here may assume as_is/to_be/parent_impact exist.
 */
export function FallbackChildrenNote({ items: fallbacks }) {
  if (!fallbacks || fallbacks.length === 0) return null;
  return (
    <div style={{ marginBottom: 14, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, fontSize: 12, color: "#92400e" }}>
      <strong>
        {fallbacks.length} sub-process{fallbacks.length === 1 ? "" : "es"} could not be redesigned
      </strong>{" "}
      — counted at their current figures, so the projection below is conservative for them.
      <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
        {fallbacks.map((c, i) => (
          <li key={c.child_process_id ?? i} style={{ marginBottom: 3 }}>
            <strong>{c.child_process_name || `Process ${c.child_process_id ?? "?"}`}:</strong>{" "}
            {c.reason || "No reason given."}
          </li>
        ))}
      </ul>
    </div>
  );
}
